/**
 * Quick Add User — the rules behind the hours editor's "Quick Add User" modal.
 *
 * The modal lets whoever is recording hours on a work order add the people
 * who did the work without leaving the job: a CSR or JSR needs nothing but a
 * name and a business, while a "Full User" is a real sign-in account and needs
 * an email, phone and password up front. Keeping the type table, validation
 * and payload shaping here (rather than inside the component) means the rules
 * are testable on their own and the modal stays about layout.
 *
 * Mirrors the backend: UserController@store decides whether an email is
 * required from the roles being assigned (Role::SIGN_IN_OPTIONAL_ROLES), and
 * getAllowedRolesForUser decides which roles the creator may hand out at all.
 */
import { ROLE_DESCRIPTIONS } from './constants';
import { generateTempPassword, isValidEmail } from './tempPassword';
import { validatePassword } from './validatePassword';
import type { CreateUserPayload } from '../services/userService';

/* ============================================
 * PERSON TYPES
 * ============================================ */

/** The three kinds of person the modal can add. */
export type QuickAddPersonType = 'csr' | 'jsr' | 'full';

/** One entry in the type picker, and the role it creates. */
export interface QuickAddTypeConfig {
	value: QuickAddPersonType;
	/** Text on the segmented control. */
	label: string;
	/** The single role slug the person is created with. */
	role: string;
	/** True for a sign-in account: email, phone and password become required. */
	credentialsRequired: boolean;
	/** One-liner explaining the choice (tooltip + helper copy). */
	description: string;
}

/**
 * The type table, in display order. CSR comes first because it is the default.
 *
 * "Full User" starts as a Technician — the basic field-worker account that
 * sees and completes the jobs it is put on. Anything with more reach than that
 * (manager, admin …) is a deliberate decision that belongs on Admin → Users,
 * not on a shortcut inside the hours editor.
 */
export const QUICK_ADD_TYPES: readonly QuickAddTypeConfig[] = [
	{ value: 'csr', label: 'CSR', role: 'csr', credentialsRequired: false, description: ROLE_DESCRIPTIONS.csr },
	{ value: 'jsr', label: 'JSR', role: 'jsr', credentialsRequired: false, description: ROLE_DESCRIPTIONS.jsr },
	{ value: 'full', label: 'Full User', role: 'technician', credentialsRequired: true, description: 'A sign-in account (Technician). Sees and completes the jobs assigned to them.' },
];

/** The type a fresh entry starts on when the creator is allowed it. */
export const DEFAULT_QUICK_ADD_TYPE: QuickAddPersonType = 'csr';

/** Look up a type's config. */
export function quickAddType(value: QuickAddPersonType): QuickAddTypeConfig {
	return QUICK_ADD_TYPES.find((t) => t.value === value) ?? QUICK_ADD_TYPES[0];
}

/**
 * The types this creator may actually use, given the role slugs the server
 * says they can assign (GET /users/available-roles). A manager, for example,
 * may create technicians but not CSRs/JSRs, so they only see "Full User".
 */
export function availableQuickAddTypes(assignableRoles: readonly string[]): QuickAddTypeConfig[] {
	return QUICK_ADD_TYPES.filter((t) => assignableRoles.includes(t.role));
}

/* ============================================
 * DRAFTS
 * ============================================ */

/** One person being filled in on the modal. All strings so inputs stay controlled. */
export interface QuickAddDraft {
	/** Stable React key — drafts can be removed from the middle of the list. */
	key: string;
	type: QuickAddPersonType;
	firstName: string;
	lastName: string;
	/** Primary business (company id as a string; '' = none picked). */
	companyId: string;
	email: string;
	phone: string;
	password: string;
}

/** Field-keyed validation messages for one draft. */
export type QuickAddDraftErrors = Partial<Record<'firstName' | 'lastName' | 'companyId' | 'email' | 'phone' | 'password', string>>;

let draftCounter = 0;

/**
 * A blank entry. A Full User starts with a friendly generated password so the
 * creator does not have to invent one; CSR/JSR start with none (they only need
 * one if they are also given an email to sign in with).
 */
export function emptyQuickAddDraft(type: QuickAddPersonType, companyId: string): QuickAddDraft {
	draftCounter += 1;
	return {
		key: `quick-add-${draftCounter}`,
		type,
		firstName: '',
		lastName: '',
		companyId,
		email: '',
		phone: '',
		password: quickAddType(type).credentialsRequired ? generateTempPassword() : '',
	};
}

/**
 * Switch a draft's type, keeping everything already typed. Moving to Full
 * User fills in a generated password if the field is still empty.
 */
export function changeQuickAddType(draft: QuickAddDraft, type: QuickAddPersonType): QuickAddDraft {
	const needsPassword = quickAddType(type).credentialsRequired && !draft.password;
	return { ...draft, type, password: needsPassword ? generateTempPassword() : draft.password };
}

/* ============================================
 * VALIDATION
 * ============================================ */

/**
 * Check one draft. Returns an empty object when it is ready to send.
 *
 * - Everyone needs a first and last name and a primary business.
 * - Full User: email, phone and password are all required.
 * - CSR/JSR: email, phone and password are optional — but a password with no
 *   email is refused, because email is the sign-in identifier and the
 *   password would be a secret nobody could use.
 * - Any password given must meet the app's password policy.
 */
export function validateQuickAddDraft(draft: QuickAddDraft): QuickAddDraftErrors {
	const errors: QuickAddDraftErrors = {};
	const required = quickAddType(draft.type).credentialsRequired;
	const email = draft.email.trim();
	const phone = draft.phone.trim();
	const password = draft.password;

	if (!draft.firstName.trim()) errors.firstName = 'First name is required.';
	if (!draft.lastName.trim()) errors.lastName = 'Last name is required.';
	/* Every quick-added person lands in a business — it is what puts them in
	   the assignee pickers of the people who work that business. */
	if (!draft.companyId) errors.companyId = 'Pick a primary business.';

	if (!email) {
		if (required) errors.email = 'Email is required for a Full User.';
	} else if (!isValidEmail(email)) {
		errors.email = 'Enter a valid email address.';
	}

	if (!phone && required) errors.phone = 'Phone is required for a Full User.';
	/* users.phone is varchar(20) server-side. */
	if (phone.length > 20) errors.phone = 'Phone must be 20 characters or fewer.';

	if (!password) {
		if (required) errors.password = 'Password is required for a Full User.';
	} else if (!email) {
		errors.password = 'Add an email so they can sign in with this password.';
	} else {
		const result = validatePassword(password);
		if (!result.isValid) errors.password = result.errors[0];
	}

	return errors;
}

/* ============================================
 * PAYLOAD
 * ============================================ */

/**
 * Shape a valid draft into the POST /users payload.
 *
 * The email goes as null, not '', when blank — null is what keeps the unique
 * index treating several name-only people as distinct rows. The password is
 * only sent alongside an email; with an email but no password the server
 * generates a temporary one and emails it.
 */
export function quickAddPayload(draft: QuickAddDraft): CreateUserPayload {
	const config = quickAddType(draft.type);
	const email = draft.email.trim();
	return {
		firstName: draft.firstName.trim(),
		lastName: draft.lastName.trim(),
		email: email || null,
		phone: draft.phone.trim() || undefined,
		role: config.role,
		roles: [config.role],
		password: email && draft.password ? draft.password : undefined,
		companyId: draft.companyId ? Number(draft.companyId) : undefined,
	};
}

/* ============================================
 * WIRING — what the pickers and hours editors share
 * ============================================ */

/** A person handed back by quick-add. */
export interface QuickAddedPersonRef {
	/** User id, as a string — the key in every hours map and picker. */
	id: string;
	/** Display name ("First Last"). */
	name: string;
	/** Email, when one was given (null for a name-only CSR/JSR). */
	email?: string | null;
	/** Phone, when one was given. */
	phone?: string | null;
}

/**
 * The `quickAdd` prop every people picker takes. Passing it (even `{}`)
 * turns quick-add on for that picker; `companyId` is the work order's
 * business, pre-selected as each new person's primary business. Whether the
 * viewer may create people at all is decided by the QuickAddUsers context,
 * so a picker only ever needs to say "offer it here".
 */
export interface QuickAddOptions {
	companyId?: string | number | null;
}

/**
 * Split what someone typed into a search box into a first and last name, so
 * "Dana Ortiz" typed into the assignee search pre-fills the new person. The
 * last word is the last name; everything before it the first name.
 */
export function splitName(text: string): { firstName: string; lastName: string } {
	const words = text.trim().split(/\s+/).filter(Boolean);
	if (words.length === 0) return { firstName: '', lastName: '' };
	if (words.length === 1) return { firstName: words[0], lastName: '' };
	return { firstName: words.slice(0, -1).join(' '), lastName: words[words.length - 1] };
}

/**
 * Put quick-added people on a crew. The first becomes primary when nobody is
 * assigned yet; everyone else is appended to the additional list, skipping
 * anyone already on the job. Shared by the plain assignee pickers and the
 * hours editor so both treat new people the same way.
 */
export function addPeopleToCrew(primaryId: string, additionalIds: string[], people: QuickAddedPersonRef[], { fillPrimary = true }: { fillPrimary?: boolean } = {}): { primaryId: string; additionalIds: string[] } {
	const onJob = new Set<string>([primaryId, ...additionalIds].filter(Boolean));
	const fresh = people.map((p) => p.id).filter((id) => !onJob.has(id));
	if (fresh.length === 0) return { primaryId, additionalIds };
	/* fillPrimary: false — the caller doesn't own the primary slot (the field
	   panel, where who leads the job is a dispatch decision), so everyone
	   joins as additional crew. */
	if (!primaryId && fillPrimary) {
		const [first, ...rest] = fresh;
		return { primaryId: first, additionalIds: [...additionalIds, ...rest] };
	}
	return { primaryId, additionalIds: [...additionalIds, ...fresh] };
}
